// This is a part of the Cadcorp SIS source code. 
// Copyright (c) NEC Software Solutions UK Limited 1991-2026 
const emptyStringRegEx = /^\s*\S.*$/;
const boundedRegEx = /(\b[4-9])|(\b[1][0-9])|(\b[2][0-5])/g;
const countryCodeRegEx = /^([a-z]{2},?)+$/gi;
const intRegEx = /^\-?[0-9]*$/;
const unsignedIntRegEx = /^[0-9]*$/;
const unsignedDecimalRegEx = /^[0-9]+(\.[0-9]+)*?$/;
const nonZeroUnsignedIntRegEx = /^[1-9][0-9]*$/;
const floatRegEx = /^\-?[0-9]+(\.[0-9]+)?$/;
const dateRegEx = /(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2})[+-](\d{2})\:(\d{2})/;
const urlRegEx = /^https?:\/\/[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)*(:[0-9]+)?(\/.*)?$/i;
const emailRegEx = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
const postcodeRegEx = /^([a-z0-9]{2,4}) ([a-z0-9]{3})$/gi;

/**
 * Validation rules for Vuetify
 */

function validateWithRegExp(value: string, regExp: RegExp): boolean {
    let returnVal = false;
    if (value !== null) {
        const txtFound = value.match(regExp);
        returnVal = txtFound !== null;
    }
    return returnVal;
}
export function validate(value: string, regEx: RegExp, error: string) {
    let isValid: boolean | string = notEmpty(value);
    if (isValid === true) {
        isValid = validateWithRegExp(value, regEx);
        if (!isValid) {
            isValid = error;
        }
    }
    return isValid;
}

/**
 * Checks that the suppiled value is not empty
 * @param {string} value The value to check
 * @returns {boolean|string} True if value is not empty, otherwise returns the key to the error text
 */
export function notEmpty(value: string): boolean | string { 
    let retVal: boolean | string = false;
    // Checks for null & undefined
    if (value) { 
        // Gets rid of whitespace
        let val = value.trim(); 

        // Checks the length
        retVal = val.length > 0 || 'webmap.messages.validation.notEmpty';
    }
    else
        retVal = 'webmap.messages.validation.notEmpty';
    return retVal;
}

/**
 * Checks that the suppiled value is not empty
 * @param {Array<T>} value The value to check
 * @returns {boolean|string} True if value is not empty, otherwise returns the key to the error text
 */
export function notEmptyArray<T>(value: Array<T>): boolean | string {
    let retVal: boolean | string = false;
    // Checks for null & undefined
    if (value) {
        // Checks the length
        retVal = value.length > 0 || 'webmap.messages.validation.notEmpty';
    }
    else
        retVal = 'webmap.messages.validation.notEmpty';
    return retVal;
}

/**
 * Checks that the supplied value is an integer value
 * @param {string} value The value to check
 * @returns {boolean|string} True if value is an integer, otherwise returns the key to the error text
 */
export function isInteger(value: string): boolean | string {
    return validate(value, intRegEx, 'webmap.messages.validation.integerOnly');
}

/**
 * Checks that the supplied value is an unsigned integer value
 * @param {string} value The value to check
 * @returns {boolean|string} True if value is an unsigned integer, otherwise returns the key to the error text
 */
export function isUnsignedInteger( value: string): boolean | string {
    return validate(value, unsignedIntRegEx, 'webmap.messages.validation.positiveIntegerOnly');
}

/**
 * Checks that the supplied value is an unsigned decimal value
 * @param {string} value The value to check
 * @returns {boolean|string} True if value is an unsigned decimal, otherwise returns the key to the error text
 */
export function isUnsignedDecimal(value: string): boolean | string {
    return validate(value, unsignedDecimalRegEx, 'webmap.messages.validation.positiveDecimalOnly');
}

/**
 * Checks that the supplied value is an integer value greater than 0
 * @param {string} value The value to check
 * @returns {boolean|string} True if value is an integer, otherwise returns the key to the error text
 */
export function isNonZeroUnsignedInteger(value: string): boolean | string {
    return validate(value, nonZeroUnsignedIntRegEx, 'webmap.messages.validation.nonZeroPositiveIntegerOnly');
}

/**
* Checks to see if a string is a number 
* @function
* @param {string} value - The string to check
* @returns {boolean} True if the string is a number, otherwise returns the key to the error text
*/
export function isNumber(value: string): boolean | string {
    return validate(value, floatRegEx, 'webmap.messages.validation.numericOnly');
}

/**
* Checks to see if a string is a date 
* @function
* @param {string} value - The string to check
* @returns {boolean} True if the string is a date, otherwise returns the key to the error text
*/
export function isDate( value: string): boolean | string {
    let isValid = validate(value, dateRegEx, 'webmap.messages.validation.date');
    if (isValid === true) {
        // final check create a date object 
        try {
            const d = new Date(value);
        } catch (err) {
            isValid = 'webmap.messages.validation.date';
        }
   }
    return isValid;
}

/**
* Checks to see if a string is a url 
* @function
* @param {string} value - The string to check
* @returns {boolean} True if the string is a url, otherwise returns the key to the error text
*/
export function isUrl(value: string): boolean | string {
    return validate(value, urlRegEx, 'webmap.messages.validation.url');
};

/**
* Checks to see if a string is an email address 
* @function
* @param {string} value - The string to check
* @returns {boolean} True if the string is an email address, otherwise returns the key to the error text
*/
export function isEmail(value: string): boolean | string {
    return validate(value, emailRegEx, 'webmap.messages.validation.email');
};

/**
* Checks to see if a string is a postcode
* @function
* @param {string} value - The string to check
* @returns {boolean} True if the string is a postcode, otherwise returns the key to the error text
*/
export function isPostcode(value: string): boolean | string {
    return validate(value, postcodeRegEx, 'webmap.messages.validation.postcode');
}

/**
* Checks to see if a string is greater than or equal to 4, but less than or equal to 25.
* @function
* @param {string} value - The string to check
* @returns {boolean} True if the string is >= 4 && <= 25, otherwise returns the key to the error text
*/
export function isBounded(value: string): boolean | string {
    return validate(value, boundedRegEx, 'webmap.messages.validation.bounded');
};

/**
* Checks to see if a string is a two-letter country code. Following the rules of ISO 3166-1 alpha-2.
* @function
* @param {string} value - The string to check
* @returns {boolean} True if the string is a two-letter code, otherwise returns the key to the error text
*/
export function isCountryCode(value: string): boolean | string {
    return validate(value, countryCodeRegEx, 'webmap.messages.validation.countryCode');
};

